import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "react-toastify";
import { useSelector } from "react-redux";
import swal from "sweetalert";
import { Button, Form, Modal } from "react-bootstrap";
import moment from "moment";
import { DataTable } from "primereact/datatable";
import { Paginator } from "primereact/paginator";
import { Column } from "primereact/column";
import { IconField } from "primereact/iconfield";
import { InputIcon } from "primereact/inputicon";
import { InputText } from "primereact/inputtext";
import * as XLSX from "xlsx";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faPrint,
  faFileExcel,
  faFilePdf,
  faPlus,
  faExternalLinkAlt,
} from "@fortawesome/free-solid-svg-icons";
import logo from "../../../assets/img/reactlogo.png";

const todayStr = new Date().toISOString().split("T")[0];

function numberToWords(num) {
  const n = parseInt(num, 10);
  if (isNaN(n) || n <= 0) return "Zero Rupees Only";
  const a = [
    "",
    "One",
    "Two",
    "Three",
    "Four",
    "Five",
    "Six",
    "Seven",
    "Eight",
    "Nine",
    "Ten",
    "Eleven",
    "Twelve",
    "Thirteen",
    "Fourteen",
    "Fifteen",
    "Sixteen",
    "Seventeen",
    "Eighteen",
    "Nineteen",
  ];
  const b = [
    "",
    "",
    "Twenty",
    "Thirty",
    "Forty",
    "Fifty",
    "Sixty",
    "Seventy",
    "Eighty",
    "Ninety",
  ];

  function inWords(val) {
    if (val < 20) return a[val];
    if (val < 100) return b[Math.floor(val / 10)] + (val % 10 ? " " + a[val % 10] : "");
    if (val < 1000)
      return a[Math.floor(val / 100)] + " Hundred" + (val % 100 ? " " + inWords(val % 100) : "");
    if (val < 100000)
      return (
        inWords(Math.floor(val / 1000)) + " Thousand" + (val % 1000 ? " " + inWords(val % 1000) : "")
      );
    if (val < 10000000)
      return (
        inWords(Math.floor(val / 100000)) + " Lakh" + (val % 100000 ? " " + inWords(val % 100000) : "")
      );
    return (
      inWords(Math.floor(val / 10000000)) +
      " Crore" +
      (val % 10000000 ? " " + inWords(val % 10000000) : "")
    );
  }
  return inWords(n) + " Rupees Only";
}

function CashVoucher() {
  const currentUser = useSelector((state) => state.data);

  // Search & Filter state
  const [startDate, setStartDate] = useState(todayStr);
  const [endDate, setEndDate] = useState(todayStr);
  const [walletTypeFilter, setWalletTypeFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  const [searchQuery, setSearchQuery] = useState("");
  const [globalFilter, setGlobalFilter] = useState(null);

  // Table Data state
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [summary, setSummary] = useState({
    totalPending: 0,
    totalApproved: 0,
    totalRejected: 0,
    grandTotal: 0,
  });
  const [pageData, setPageData] = useState({
    first: 0,
    rows: 20,
    totalRecords: 0,
  });

  // Modal States
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showPrintModal, setShowPrintModal] = useState(false);
  const [selectedVoucherForPrint, setSelectedVoucherForPrint] = useState(null);

  // Create Form State
  const [userQuery, setUserQuery] = useState(
    currentUser && currentUser.name
      ? currentUser.mobile
        ? `${currentUser.name} (${currentUser.mobile})`
        : currentUser.name
      : ""
  );
  const [userSuggestions, setUserSuggestions] = useState([]);
  const [selectedUserId, setSelectedUserId] = useState(currentUser?._id || "");
  const [customerName, setCustomerName] = useState("");
  const [mobileNumber, setMobileNumber] = useState("");
  const [walletType, setWalletType] = useState("remitance");
  const [receivedBy, setReceivedBy] = useState(currentUser?.name || "Admin");
  const [voucherDate, setVoucherDate] = useState(todayStr);
  const [denominations, setDenominations] = useState({
    note_500: 0,
    note_200: 0,
    note_100: 0,
    note_50: 0,
    note_20: 0,
    note_10: 0,
    note_5: 0,
    note_2: 0,
    note_1: 0,
  });
  const [amountInWordsCustom, setAmountInWordsCustom] = useState("");
  const [message, setMessage] = useState("");
  const [totalReturn, setTotalReturn] = useState("");

  // Total Amount Calculation
  const grossAmount =
    (parseInt(denominations.note_500) || 0) * 500 +
    (parseInt(denominations.note_200) || 0) * 200 +
    (parseInt(denominations.note_100) || 0) * 100 +
    (parseInt(denominations.note_50) || 0) * 50 +
    (parseInt(denominations.note_20) || 0) * 20 +
    (parseInt(denominations.note_10) || 0) * 10 +
    (parseInt(denominations.note_5) || 0) * 5 +
    (parseInt(denominations.note_2) || 0) * 2 +
    (parseInt(denominations.note_1) || 0) * 1;

  const returnAmt = parseFloat(totalReturn) || 0;
  const totalAmount = Math.max(0, grossAmount - returnAmt);

  useEffect(() => {
    setAmountInWordsCustom(numberToWords(totalAmount));
  }, [totalAmount]);

  // Search User Autocomplete
  useEffect(() => {
    if (userQuery.length < 2) {
      setUserSuggestions([]);
      return;
    }
    axios
      .get(`/users/search?q=${encodeURIComponent(userQuery)}`)
      .then((res) => {
        setUserSuggestions(res.data.users || []);
      })
      .catch(() => setUserSuggestions([]));
  }, [userQuery]);

  useEffect(() => {
    if (currentUser && currentUser._id && !selectedUserId) {
      setSelectedUserId(currentUser._id);
      setUserQuery(
        currentUser.mobile
          ? `${currentUser.name} (${currentUser.mobile})`
          : currentUser.name || ""
      );
      if (currentUser.name) setReceivedBy(currentUser.name);
    }
  }, [currentUser]);

  const selectUser = (user) => {
    setSelectedUserId(user._id);
    setUserQuery(user.mobile ? `${user.name} (${user.mobile})` : user.name || "");
    setUserSuggestions([]);
  };

  // Fetch Cash Vouchers
  const fetchVouchers = async (reqBody = {}) => {
    try {
      setLoading(true);
      const payload = {
        start_date: startDate,
        end_date: endDate,
        wallet_type: walletTypeFilter,
        status: statusFilter,
        search_query: searchQuery,
        first: reqBody.first !== undefined ? reqBody.first : pageData.first,
        rows: reqBody.rows !== undefined ? reqBody.rows : pageData.rows,
      };
      const res = await axios.post("/cash-voucher/list", payload);
      if (res.data.status) {
        setData(res.data.data);
        setPageData({
          first: res.data.first,
          rows: res.data.rows,
          totalRecords: res.data.totalRecords,
        });
        if (res.data.records) {
          setSummary(res.data.records);
        }
      }
    } catch (err) {
      toast.error(err?.response?.data?.message || "Failed to load voucher list.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchVouchers();
  }, []);

  const handleSearchSubmit = (e) => {
    e.preventDefault();
    fetchVouchers({ first: 0 });
  };

  const onPageChange = (e) => {
    fetchVouchers({ first: e.first, rows: e.rows });
  };

  // Denomination input change
  const handleDenomChange = (denomKey, value) => {
    const val = parseInt(value, 10);
    setDenominations({
      ...denominations,
      [denomKey]: isNaN(val) || val < 0 ? 0 : val,
    });
  };

  // Submit Create Voucher
  const handleCreateVoucher = async (e) => {
    e.preventDefault();
    if (!selectedUserId) {
      toast.error("Please search and select a registered user.");
      return;
    }
    if (!customerName || !mobileNumber) {
      toast.error("Customer Name and Mobile Number are required.");
      return;
    }
    if (totalAmount <= 0) {
      toast.error("Total amount must be greater than 0.");
      return;
    }

    const confirmCreate = await swal({
      title: "Confirm Cash Received Voucher",
      text: `Are you sure you want to create a Cash Received Voucher of ₹ ${totalAmount} for ${customerName} (${mobileNumber}) in ${walletType.toUpperCase()} wallet?`,
      icon: "warning",
      buttons: ["Cancel", "Yes, Create Voucher"],
      dangerMode: true,
    });

    if (!confirmCreate) return;

    try {
      const payload = {
        customer_name: customerName,
        mobile_number: mobileNumber,
        user_id: selectedUserId || null,
        wallet_type: walletType,
        received_by: receivedBy,
        voucher_date: voucherDate,
        denominations,
        total_amount: totalAmount,
        total_return: parseFloat(totalReturn) || 0,
        amount_in_words: amountInWordsCustom,
        message,
      };

      const res = await axios.post("/cash-voucher/create", payload);
      if (res.data.status) {
        swal(
          "Success",
          "Cash Voucher created! Request sent to AddMoney (/admin/addMoney) for approval.",
          "success"
        );
        setShowCreateModal(false);
        resetCreateForm();
        fetchVouchers();
      }
    } catch (err) {
      toast.error(err?.response?.data?.message || "Error creating voucher.");
    }
  };

  const resetCreateForm = () => {
    if (currentUser && currentUser._id) {
      setSelectedUserId(currentUser._id);
      setUserQuery(
        currentUser.mobile
          ? `${currentUser.name} (${currentUser.mobile})`
          : currentUser.name || ""
      );
      setReceivedBy(currentUser.name || "Admin");
    } else {
      setUserQuery("");
      setSelectedUserId("");
      setReceivedBy("Admin");
    }
    setCustomerName("");
    setMobileNumber("");
    setWalletType("remitance");
    setVoucherDate(todayStr);
    setDenominations({
      note_500: 0,
      note_200: 0,
      note_100: 0,
      note_50: 0,
      note_20: 0,
      note_10: 0,
      note_5: 0,
      note_2: 0,
      note_1: 0,
    });
    setAmountInWordsCustom("");
    setMessage("");
    setTotalReturn("");
  };

  // EXPORT TO PDF (WITH TOTAL CASH AT BOTTOM)
  const handleExportPDF = async () => {
    try {
      const payload = {
        start_date: startDate,
        end_date: endDate,
        wallet_type: walletTypeFilter,
        status: statusFilter,
      };

      const res = await axios.post("/cash-voucher/export", payload);
      if (res.data.status && res.data.data.length > 0) {
        const records = res.data.data;
        const stats = res.data.summary || summary;

        const doc = new jsPDF({ orientation: "landscape" });

        // Header Title
        doc.setFontSize(16);
        doc.setTextColor(0, 32, 96); // #002060
        doc.text("PARGATI ONLINE SERVICE PRIVATE LIMITED", 14, 15);

        doc.setFontSize(11);
        doc.setTextColor(100, 100, 100);
        doc.text(
          `CASH RECEIVED VOUCHERS REPORT (${moment(startDate).format("DD-MMM-YYYY")} to ${moment(
            endDate
          ).format("DD-MMM-YYYY")})`,
          14,
          22
        );

        const tableColumn = [
          "#",
          "Voucher No",
          "Date",
          "Customer Name",
          "Mobile",
          "Target Wallet",
          "Amount (INR)",
          "Message",
          "Status",
          "Received By",
        ];

        const tableRows = records.map((item) => [
          item.sno,
          item.voucher_no,
          item.voucher_date,
          item.customer_name,
          item.mobile_number,
          item.wallet_type,
          `INR ${item.total_amount}`,
          item.message || "-",
          item.status,
          item.received_by,
        ]);

        autoTable(doc, {
          startY: 28,
          head: [tableColumn],
          body: tableRows,
          theme: "grid",
          headStyles: {
            fillColor: [0, 32, 96],
            textColor: [255, 255, 255],
            fontStyle: "bold",
            halign: "center",
          },
          columnStyles: {
            0: { halign: "center", cellWidth: 12 },
            6: { halign: "right", fontStyle: "bold" },
            7: { halign: "center", fontStyle: "bold" },
          },
          foot: [
            [
              "",
              "",
              "",
              "",
              "",
              "TOTAL APPROVED CASH",
              `INR ${(stats.totalApproved || 0).toFixed(2)}`,
              "APPROVED",
              "",
            ],
            [
              "",
              "",
              "",
              "",
              "",
              "TOTAL PENDING CASH",
              `INR ${(stats.totalPending || 0).toFixed(2)}`,
              "PENDING",
              "",
            ],
            [
              "",
              "",
              "",
              "",
              "",
              "TOTAL REJECTED CASH",
              `INR ${(stats.totalRejected || 0).toFixed(2)}`,
              "REJECTED",
              "",
            ],
            [
              "SUMMARY",
              `Total Records: ${records.length}`,
              "",
              "",
              "",
              "TOTAL CASH COLLECTED",
              `INR ${(stats.grandTotal || 0).toFixed(2)}`,
              "GRAND TOTAL",
              "",
            ],
          ],
          footStyles: {
            fillColor: [0, 32, 96],
            textColor: [255, 255, 255],
            fontStyle: "bold",
            fontSize: 10,
          },
        });

        doc.save(`Cash_Voucher_Report_${startDate}_to_${endDate}.pdf`);
        toast.success(`Exported ${records.length} records to PDF successfully.`);
      } else {
        toast.info("No records found for the selected date range to export.");
      }
    } catch (err) {
      toast.error("Failed to export PDF records.");
    }
  };

  // EXPORT TO EXCEL
  const handleExportExcel = async () => {
    try {
      const payload = {
        start_date: startDate,
        end_date: endDate,
        wallet_type: walletTypeFilter,
        status: statusFilter,
      };

      const res = await axios.post("/cash-voucher/export", payload);
      if (res.data.status && res.data.data.length > 0) {
        const worksheet = XLSX.utils.json_to_sheet(res.data.data);
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, "Cash_Vouchers");
        XLSX.writeFile(
          workbook,
          `Cash_Vouchers_${startDate}_to_${endDate}.xlsx`
        );
        toast.success(`Exported ${res.data.data.length} records to Excel.`);
      } else {
        toast.info("No records found for the selected date range.");
      }
    } catch (err) {
      toast.error("Failed to export Excel records.");
    }
  };

  // Open Printable Voucher Modal
  const openPrintModal = (voucher) => {
    setSelectedVoucherForPrint(voucher);
    setShowPrintModal(true);
  };

  const triggerWindowPrint = () => {
    window.print();
  };

  const headerTable = (
    <div className="d-flex flex-wrap gap-2 align-items-center justify-content-between">
      <IconField iconPosition="right">
        <InputIcon className="pi pi-search" />
        <InputText
          type="search"
          onInput={(e) => setGlobalFilter(e.target.value)}
          placeholder="Filter in current table..."
        />
      </IconField>
    </div>
  );

  return (
    <div className="content-wrapper">
      {/* Printable Voucher Section */}
      <style>{`
        @media print {
          body * {
            visibility: hidden;
          }
          #printableVoucherSlip, #printableVoucherSlip * {
            visibility: visible;
          }
          #printableVoucherSlip {
            position: absolute;
            left: 0;
            top: 0;
            width: 100%;
          }
          .no-print {
            display: none !important;
          }
        }
        .voucher-card-box {
          border: 2px solid #002060;
          border-radius: 12px;
          background: #ffffff;
          padding: 20px;
          color: #002060;
          font-family: Arial, sans-serif;
        }
        .voucher-header-title {
          font-weight: 900;
          color: #002060;
          font-size: 24px;
          letter-spacing: 0.5px;
        }
        .voucher-banner-box {
          background-color: #002060;
          color: #ffffff;
          font-weight: 800;
          font-size: 20px;
          padding: 8px 16px;
          border-radius: 6px;
          text-align: center;
          letter-spacing: 1px;
        }
        .voucher-table-border, .voucher-table-border th, .voucher-table-border td {
          border: 1px solid #002060 !important;
        }
        .voucher-total-row {
          background-color: #e6f0ff;
          font-weight: bold;
        }
      `}</style>

      <div className="row">
        {/* Filters Card */}
        <div className="col-lg-12 grid-margin stretch-card">
          <div className="card">
            <div className="card-body">
              <div className="d-flex justify-content-between align-items-center mb-3">
                <h4 className="card-title mb-0">Cash Voucher Records</h4>
                <div className="d-flex gap-2">
                  <Button
                    variant="danger"
                    onClick={handleExportPDF}
                    className="me-2"
                  >
                    <FontAwesomeIcon icon={faFilePdf} className="me-1" /> Export PDF
                  </Button>
                  <Button
                    variant="success"
                    onClick={handleExportExcel}
                    className="me-2"
                  >
                    <FontAwesomeIcon icon={faFileExcel} className="me-1" /> Export Excel
                  </Button>
                  <Button
                    variant="primary"
                    onClick={() => {
                      resetCreateForm();
                      setShowCreateModal(true);
                    }}
                  >
                    <FontAwesomeIcon icon={faPlus} className="me-1" /> Create Voucher
                  </Button>
                </div>
              </div>

              <form onSubmit={handleSearchSubmit}>
                <div className="row g-2 align-items-end">
                  <div className="col-lg-3 col-md-3">
                    <label className="fw-bold">From Date</label>
                    <input
                      type="date"
                      className="form-control"
                      value={startDate}
                      onChange={(e) => setStartDate(e.target.value)}
                    />
                  </div>
                  <div className="col-lg-3 col-md-3">
                    <label className="fw-bold">To Date</label>
                    <input
                      type="date"
                      className="form-control"
                      value={endDate}
                      onChange={(e) => setEndDate(e.target.value)}
                    />
                  </div>
                  <div className="col-lg-2 col-md-3">
                    <label className="fw-bold">Wallet Type</label>
                    <select
                      className="form-control"
                      value={walletTypeFilter}
                      onChange={(e) => setWalletTypeFilter(e.target.value)}
                    >
                      <option value="all">ALL</option>
                      <option value="remitance">REMITTANCE</option>
                      <option value="recharge">RECHARGE</option>
                    </select>
                  </div>
                  <div className="col-lg-2 col-md-3">
                    <label className="fw-bold">Status</label>
                    <select
                      className="form-control"
                      value={statusFilter}
                      onChange={(e) => setStatusFilter(e.target.value)}
                    >
                      <option value="all">ALL</option>
                      <option value="pending">Pending</option>
                      <option value="approved">Approved</option>
                      <option value="rejected">Rejected</option>
                    </select>
                  </div>
                  <div className="col-lg-2 col-md-3">
                    <button type="submit" className="btn btn-primary w-100">
                      Search
                    </button>
                  </div>
                </div>
              </form>
            </div>
          </div>
        </div>

        {/* Summary Stats */}
        <div className="col-lg-12 grid-margin stretch-card">
          <div className="card">
            <div className="card-body">
              <h4 className="card-title">Voucher Summary</h4>
              <div className="row text-center">
                <div className="col-lg-3 col-md-6 mb-2">
                  <div className="p-3 border rounded bg-light">
                    <small className="text-muted d-block fw-bold">TOTAL PENDING</small>
                    <span className="h4 text-warning fw-bold">
                      ₹ {(summary.totalPending || 0).toFixed(2)}
                    </span>
                  </div>
                </div>
                <div className="col-lg-3 col-md-6 mb-2">
                  <div className="p-3 border rounded bg-light">
                    <small className="text-muted d-block fw-bold">TOTAL APPROVED</small>
                    <span className="h4 text-success fw-bold">
                      ₹ {(summary.totalApproved || 0).toFixed(2)}
                    </span>
                  </div>
                </div>
                <div className="col-lg-3 col-md-6 mb-2">
                  <div className="p-3 border rounded bg-light">
                    <small className="text-muted d-block fw-bold">TOTAL REJECTED</small>
                    <span className="h4 text-danger fw-bold">
                      ₹ {(summary.totalRejected || 0).toFixed(2)}
                    </span>
                  </div>
                </div>
                <div className="col-lg-3 col-md-6 mb-2">
                  <div className="p-3 border rounded bg-light">
                    <small className="text-muted d-block fw-bold">TOTAL CASH COLLECTED</small>
                    <span className="h4 text-primary fw-bold">
                      ₹ {(summary.grandTotal || 0).toFixed(2)}
                    </span>
                  </div>
                </div>
              </div>

              {/* Data Table */}
              <DataTable
                value={data}
                size="small"
                globalFilter={globalFilter}
                header={headerTable}
                className="custom-table mt-3"
                loading={loading}
              >
                <Column
                  field="index"
                  header="#"
                  body={(rowData, { rowIndex }) => (
                    <span>{pageData.first + rowIndex + 1}</span>
                  )}
                />
                <Column
                  field="voucher_no"
                  header="Voucher No"
                  sortable
                  body={(rowData) => (
                    <strong className="text-primary">{rowData.voucher_no}</strong>
                  )}
                />
                <Column
                  field="voucher_date"
                  header="Date"
                  sortable
                  body={(rowData) => (
                    <span>
                      {moment(rowData.voucher_date).format("MMM-DD-YYYY")}
                      <br />
                      <small className="text-muted">
                        {moment(rowData.created_at).format("hh:mm A")}
                      </small>
                    </span>
                  )}
                />
                <Column
                  field="customer_name"
                  header="Customer Name (Mobile)"
                  sortable
                  body={(rowData) => (
                    <span>
                      <strong>{rowData.customer_name}</strong>
                      <br />
                      <small>({rowData.mobile_number})</small>
                    </span>
                  )}
                />
                <Column
                  field="wallet_type"
                  header="Target Wallet"
                  sortable
                  body={(rowData) => (
                    <span
                      className="badge"
                      style={{
                        backgroundColor:
                          rowData.wallet_type === "recharge" ? "#17a2b8" : "#6f42c1",
                        color: "#fff",
                      }}
                    >
                      {(rowData.wallet_type || "").toUpperCase()}
                    </span>
                  )}
                />
                <Column
                  field="total_amount"
                  header="Amount (₹)"
                  sortable
                  body={(rowData) => (
                    <strong className="text-dark">₹ {rowData.total_amount}</strong>
                  )}
                />
                <Column
                  field="message"
                  header="Message / Remarks"
                  sortable
                  body={(rowData) => (
                    <small className="text-muted">{rowData.message || "-"}</small>
                  )}
                />
                <Column
                  field="received_by"
                  header="Received By"
                  sortable
                  body={(rowData) => <span>{rowData.received_by || "Admin"}</span>}
                />
                <Column
                  field="status"
                  header="Status"
                  sortable
                  body={(rowData) => (
                    <div>
                      {rowData.status === "pending" ? (
                        <Link
                          to="/admin/addMoney"
                          className="badge bg-warning text-dark text-decoration-none"
                          title="Click to Approve/Decline on AddMoney page"
                        >
                          PENDING <FontAwesomeIcon icon={faExternalLinkAlt} size="xs" />
                        </Link>
                      ) : rowData.status === "approved" ? (
                        <span className="badge bg-success">APPROVED</span>
                      ) : (
                        <span
                          className="badge bg-danger"
                          title={rowData.reject_reason || "Rejected"}
                        >
                          REJECTED
                        </span>
                      )}
                    </div>
                  )}
                />
                <Column
                  field="action"
                  header="Action"
                  body={(rowData) => (
                    <div className="d-flex align-items-center gap-2">
                      <Button
                        variant="outline-secondary"
                        size="sm"
                        onClick={() => openPrintModal(rowData)}
                        title="View / Print Voucher Slip"
                      >
                        <FontAwesomeIcon icon={faPrint} /> Print
                      </Button>
                    </div>
                  )}
                />
              </DataTable>

              <div className="card mt-2">
                <Paginator
                  first={pageData.first}
                  rows={pageData.rows}
                  totalRecords={pageData.totalRecords}
                  rowsPerPageOptions={[10, 20, 50, 100]}
                  onPageChange={onPageChange}
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* CREATE CASH VOUCHER MODAL */}
      <Modal
        show={showCreateModal}
        onHide={() => setShowCreateModal(false)}
        centered
        size="xl"
        backdrop="static"
      >
        <Form onSubmit={handleCreateVoucher}>
          <Modal.Header closeButton className="bg-primary text-white">
            <Modal.Title className="text-white">Create Cash Received Voucher</Modal.Title>
          </Modal.Header>
          <Modal.Body style={{ background: "#f8f9fa" }}>
            <div className="voucher-card-box">
              {/* Voucher Top Header */}
              <div className="row align-items-center mb-3">
                <div className="col-md-2 text-center text-md-start">
                  <img src={logo} alt="Pargati Logo" style={{ maxHeight: "60px" }} />
                </div>
                <div className="col-md-7 text-center">
                  <h3 className="voucher-header-title mb-0">
                    PARGATI ONLINE SERVICE PRIVATE LIMITED
                  </h3>
                  <p className="mb-0 text-muted fw-bold small">
                    India &rarr; Nepal Remittance | Mobile Recharge | Bill Payment
                  </p>
                </div>
                <div className="col-md-3">
                  <div className="p-2 border rounded border-primary bg-light text-end">
                    <div>
                      <small className="fw-bold">Voucher Date:</small>
                      <input
                        type="date"
                        className="form-control form-control-sm mt-1"
                        value={voucherDate}
                        onChange={(e) => setVoucherDate(e.target.value)}
                        required
                      />
                    </div>
                  </div>
                </div>
              </div>

              {/* Banner Title */}
              <div className="voucher-banner-box mb-4">
                &equiv;&equiv;&equiv; CASH RECEIVED VOUCHER &equiv;&equiv;&equiv;
              </div>

              {/* User Search & Customer Info */}
              <div className="row g-3 mb-3">
                <div className="col-md-6">
                  <label className="fw-bold">
                    Search Registered User <span className="text-danger">*</span> :
                  </label>
                  <input
                    type="text"
                    className="form-control"
                    placeholder="Type name or mobile to search & select user..."
                    value={userQuery}
                    onChange={(e) => {
                      setUserQuery(e.target.value);
                      if (selectedUserId) setSelectedUserId("");
                    }}
                    required
                    readOnly
                  />
                  {userSuggestions.length > 0 && (
                    <ul
                      className="list-group position-absolute z-3 shadow"
                      style={{ maxHeight: "160px", overflowY: "auto", width: "45%" }}
                    >
                      {userSuggestions.map((u) => (
                        <li
                          key={u._id}
                          className="list-group-item list-group-item-action cursor-pointer py-1"
                          onClick={() => selectUser(u)}
                        >
                          <strong>{u.name}</strong> ({u.mobile})
                        </li>
                      ))}
                    </ul>
                  )}
                </div>

                <div className="col-md-6">
                  <label className="fw-bold">Target Wallet (Add Money For)</label>
                  <div className="d-flex gap-4 mt-2">
                    <div className="form-check">
                      <input
                        className="form-check-input"
                        type="radio"
                        name="walletTypeRadio"
                        id="wtRemit"
                        value="remitance"
                        checked={walletType === "remitance"}
                        onChange={() => setWalletType("remitance")}
                      />
                      <label className="form-check-label fw-bold text-purple" htmlFor="wtRemit">
                        REMITTANCE WALLET
                      </label>
                    </div>
                    <div className="form-check">
                      <input
                        className="form-check-input"
                        type="radio"
                        name="walletTypeRadio"
                        id="wtRecharge"
                        value="recharge"
                        checked={walletType === "recharge"}
                        onChange={() => setWalletType("recharge")}
                      />
                      <label className="form-check-label fw-bold text-info" htmlFor="wtRecharge">
                        RECHARGE WALLET
                      </label>
                    </div>
                  </div>
                </div>

                <div className="col-md-6">
                  <label className="fw-bold">
                    Customer Name <span className="text-danger">*</span> :
                  </label>
                  <input
                    type="text"
                    className="form-control border-bottom-only"
                    placeholder="Enter customer full name"
                    value={customerName}
                    onChange={(e) => setCustomerName(e.target.value)}
                    required
                  />
                </div>

                <div className="col-md-6">
                  <label className="fw-bold">
                    Mobile Number <span className="text-danger">*</span> :
                  </label>
                  <input
                    type="text"
                    className="form-control border-bottom-only"
                    placeholder="Enter customer mobile number"
                    value={mobileNumber}
                    onChange={(e) => setMobileNumber(e.target.value)}
                    required
                  />
                </div>
              </div>

              {/* Denominations Table */}
              <div className="table-responsive my-3">
                <table className="table table-bordered voucher-table-border mb-0">
                  <thead className="bg-primary text-white text-center">
                    <tr>
                      <th style={{ width: "30%", backgroundColor: "#002060", color: "#fff" }}>
                        DENOMINATION (₹)
                      </th>
                      <th style={{ width: "40%", backgroundColor: "#002060", color: "#fff" }}>
                        NUMBER OF NOTES
                      </th>
                      <th style={{ width: "30%", backgroundColor: "#002060", color: "#fff" }}>
                        AMOUNT (₹)
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {[
                      { key: "note_500", label: "₹ 500", val: 500 },
                      { key: "note_200", label: "₹ 200", val: 200 },
                      { key: "note_100", label: "₹ 100", val: 100 },
                      { key: "note_50", label: "₹ 50", val: 50 },
                      { key: "note_20", label: "₹ 20", val: 20 },
                      { key: "note_10", label: "₹ 10", val: 10 },
                      { key: "note_5", label: "₹ 5", val: 5 },
                      { key: "note_2", label: "₹ 2", val: 2 },
                      { key: "note_1", label: "₹ 1", val: 1 },
                    ].map((row) => {
                      const count = denominations[row.key] || 0;
                      const subTotal = count * row.val;
                      return (
                        <tr key={row.key} className="align-middle text-center">
                          <td className="fw-bold text-dark">{row.label}</td>
                          <td>
                            <input
                              type="number"
                              min="0"
                              className="form-control form-control-sm text-center"
                              value={denominations[row.key] || ""}
                              onChange={(e) => handleDenomChange(row.key, e.target.value)}
                              placeholder="0"
                            />
                          </td>
                          <td className="fw-bold text-end pe-4">₹ {subTotal}</td>
                        </tr>
                      );
                    })}
                    <tr className="align-middle">
                      <td colSpan="2" className="text-end pe-3 fw-bold">
                        GROSS CASH RECEIVED
                      </td>
                      <td className="fw-bold text-end pe-4">
                        ₹ {grossAmount}
                      </td>
                    </tr>
                    <tr className="align-middle bg-light">
                      <td colSpan="2" className="text-end pe-3 fw-bold text-danger">
                        TOTAL RETURN (₹) TO CUSTOMER
                      </td>
                      <td className="pe-2">
                        <input
                          type="number"
                          min="0"
                          className="form-control form-control-sm text-end fw-bold text-danger"
                          value={totalReturn}
                          onChange={(e) => setTotalReturn(e.target.value)}
                          placeholder="0"
                        />
                      </td>
                    </tr>
                    <tr className="voucher-total-row text-center">
                      <td colSpan="2" className="text-end pe-3 fs-5">
                        TOTAL AMOUNT
                      </td>
                      <td className="fs-5 text-end pe-4 text-primary">
                        ₹ {totalAmount}
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>

              {/* Amount in Words */}
              <div className="row my-3">
                <div className="col-md-12">
                  <label className="fw-bold">Amount in Words :</label>
                  <input
                    type="text"
                    className="form-control border-bottom-only fw-bold text-secondary"
                    value={amountInWordsCustom}
                    onChange={(e) => setAmountInWordsCustom(e.target.value)}
                    placeholder="Rupees..."
                    readOnly
                  />
                </div>
              </div>

              {/* Message / Remarks Input */}
              <div className="row my-3">
                <div className="col-md-12">
                  <label className="fw-bold">Message / Remarks (Optional) :</label>
                  <textarea
                    className="form-control"
                    rows="2"
                    placeholder="Enter any optional message or remarks..."
                    value={message}
                    onChange={(e) => setMessage(e.target.value)}
                  />
                </div>
              </div>

              {/* Received By Input */}
              <div className="row my-2">
                <div className="col-md-4">
                  <label className="fw-bold">Received By Staff/Admin :</label>
                  <input
                    type="text"
                    className="form-control"
                    value={receivedBy}
                    onChange={(e) => setReceivedBy(e.target.value)}
                    readOnly
                  />
                </div>
              </div>

              {/* Signatures Row */}
              <div className="row text-center mt-5 pt-4">
                <div className="col-md-4">
                  <div className="border-top border-dark pt-2 fw-bold">
                    Customer Signature
                  </div>
                </div>
                <div className="col-md-4">
                  <div className="border-top border-dark pt-2 fw-bold">Received By</div>
                </div>
                <div className="col-md-4">
                  <div className="border-top border-dark pt-2 fw-bold">
                    Authorized Signature
                  </div>
                </div>
              </div>
            </div>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowCreateModal(false)}>
              Cancel
            </Button>
            <Button variant="primary" type="submit">
              Create Cash Voucher
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>

      {/* PRINTABLE STYLED VOUCHER MODAL */}
      <Modal
        show={showPrintModal}
        onHide={() => setShowPrintModal(false)}
        centered
        size="lg"
      >
        <Modal.Header closeButton>
          <Modal.Title>Print Cash Voucher Slip</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedVoucherForPrint && (
            <div id="printableVoucherSlip" className="voucher-card-box">
              <div className="row align-items-center mb-3">
                <div className="col-3 text-center">
                  <img src={logo} alt="Logo" style={{ maxHeight: "50px" }} />
                </div>
                <div className="col-6 text-center">
                  <h4 className="voucher-header-title mb-0" style={{ fontSize: "18px" }}>
                    PARGATI ONLINE SERVICE PRIVATE LIMITED
                  </h4>
                  <p className="mb-0 text-muted fw-bold" style={{ fontSize: "11px" }}>
                    India &rarr; Nepal Remittance | Mobile Recharge | Bill Payment
                  </p>
                </div>
                <div className="col-3 text-end" style={{ fontSize: "12px" }}>
                  <div>
                    <strong>Voucher No :</strong> {selectedVoucherForPrint.voucher_no}
                  </div>
                  <div>
                    <strong>Date :</strong>{" "}
                    {moment(selectedVoucherForPrint.voucher_date).format("DD / MM / YYYY")}
                  </div>
                </div>
              </div>

              <div className="voucher-banner-box py-1 fs-6 mb-3">
                CASH RECEIVED VOUCHER
              </div>

              <div className="row mb-3" style={{ fontSize: "14px" }}>
                <div className="col-6">
                  <strong>Customer Name :</strong> {selectedVoucherForPrint.customer_name}
                </div>
                <div className="col-6">
                  <strong>Mobile Number :</strong> {selectedVoucherForPrint.mobile_number}
                </div>
                <div className="col-12 mt-1">
                  <strong>Target Wallet :</strong>{" "}
                  {(selectedVoucherForPrint.wallet_type || "").toUpperCase()}
                </div>
              </div>

              <table className="table table-bordered voucher-table-border text-center mb-2" style={{ fontSize: "13px" }}>
                <thead>
                  <tr style={{ backgroundColor: "#002060", color: "#fff" }}>
                    <th>DENOMINATION (₹)</th>
                    <th>NUMBER OF NOTES</th>
                    <th>AMOUNT (₹)</th>
                  </tr>
                </thead>
                <tbody>
                  {[
                    { label: "₹ 500", key: "note_500", mult: 500 },
                    { label: "₹ 200", key: "note_200", mult: 200 },
                    { label: "₹ 100", key: "note_100", mult: 100 },
                    { label: "₹ 50", key: "note_50", mult: 50 },
                    { label: "₹ 20", key: "note_20", mult: 20 },
                    { label: "₹ 10", key: "note_10", mult: 10 },
                    { label: "₹ 5", key: "note_5", mult: 5 },
                    { label: "₹ 2", key: "note_2", mult: 2 },
                    { label: "₹ 1", key: "note_1", mult: 1 },
                  ].map((d) => {
                    const cnt =
                      (selectedVoucherForPrint.denominations &&
                        selectedVoucherForPrint.denominations[d.key]) ||
                      0;
                    return (
                      <tr key={d.key}>
                        <td>{d.label}</td>
                        <td>{cnt}</td>
                        <td className="text-end pe-3">₹ {cnt * d.mult}</td>
                      </tr>
                    );
                  })}
                  {(() => {
                    const grossPrint = [
                      { key: "note_500", mult: 500 },
                      { key: "note_200", mult: 200 },
                      { key: "note_100", mult: 100 },
                      { key: "note_50", mult: 50 },
                      { key: "note_20", mult: 20 },
                      { key: "note_10", mult: 10 },
                      { key: "note_5", mult: 5 },
                      { key: "note_2", mult: 2 },
                      { key: "note_1", mult: 1 },
                    ].reduce((sum, d) => {
                      const count = (selectedVoucherForPrint.denominations && selectedVoucherForPrint.denominations[d.key]) || 0;
                      return sum + count * d.mult;
                    }, 0);
                    const returnPrint = selectedVoucherForPrint.total_return || 0;

                    return (
                      <>
                        <tr className="align-middle">
                          <td colSpan="2" className="text-end pe-3 fw-bold">
                            GROSS CASH RECEIVED
                          </td>
                          <td className="text-end pe-3 fw-bold">
                            ₹ {grossPrint}
                          </td>
                        </tr>
                        {returnPrint > 0 && (
                          <tr className="align-middle text-danger">
                            <td colSpan="2" className="text-end pe-3 fw-bold">
                              TOTAL RETURN (₹) TO CUSTOMER
                            </td>
                            <td className="text-end pe-3 fw-bold">
                              - ₹ {returnPrint}
                            </td>
                          </tr>
                        )}
                        <tr className="voucher-total-row">
                          <td colSpan="2" className="text-end pe-3 fw-bold">
                            TOTAL AMOUNT
                          </td>
                          <td className="text-end pe-3 fw-bold text-primary">
                            ₹ {selectedVoucherForPrint.total_amount}
                          </td>
                        </tr>
                      </>
                    );
                  })()}
                </tbody>
              </table>

              <div className="mb-2" style={{ fontSize: "13px" }}>
                <strong>Amount in Words :</strong>{" "}
                <span className="fw-bold">
                  {selectedVoucherForPrint.amount_in_words ||
                    numberToWords(selectedVoucherForPrint.total_amount)}
                </span>
              </div>

              <div className="mb-4" style={{ fontSize: "13px" }}>
                <strong>Message / Remarks :</strong>{" "}
                <span>{selectedVoucherForPrint.message || "-"}</span>
              </div>

              <div className="row text-center mt-5 pt-3" style={{ fontSize: "12px" }}>
                <div className="col-4">
                  <div className="border-top border-dark pt-1 fw-bold">
                    Customer Signature
                  </div>
                </div>
                <div className="col-4">
                  <div className="border-top border-dark pt-1 fw-bold">
                    Received By ({selectedVoucherForPrint.received_by || "Staff"})
                  </div>
                </div>
                <div className="col-4">
                  <div className="border-top border-dark pt-1 fw-bold">
                    Authorized Signature
                  </div>
                </div>
              </div>
            </div>
          )}
        </Modal.Body>
        <Modal.Footer className="no-print">
          <Button variant="secondary" onClick={() => setShowPrintModal(false)}>
            Close
          </Button>
          <Button variant="primary" onClick={triggerWindowPrint}>
            <FontAwesomeIcon icon={faPrint} /> Print Slip
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
}

export default CashVoucher;
